import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const RATE_META = {
  1: { label: "Insatisfecho", color: "#E53935" },
  2: { label: "Satisfecho", color: "#FB8C00" },
  3: { label: "Muy satisfecho", color: "#43A047" }
};

const SOURCE_LABEL = { ticket: "Atención (cierre de ticket)", negocio: "Tienda (post-compra)" };

const useStyles = makeStyles((theme) => ({
  filters: { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 },
  kpis: { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 },
  kpi: { flex: "1 1 160px", padding: "12px 16px", borderRadius: 10, textAlign: "center" },
  kpiValue: { fontSize: 26, fontWeight: 700 },
  section: { padding: 16, borderRadius: 10, marginBottom: 16, overflow: "hidden", minWidth: 0, maxWidth: "100%" },
  table: {
    width: "100%",
    tableLayout: "fixed",
    "& td, & th": { whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }
  },
  cell: { whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" },
  sectionTitle: { fontWeight: 700, marginBottom: 12 },
  barTrack: { height: 10, borderRadius: 5, background: theme.palette.action.hover, overflow: "hidden", flex: 1 },
  clickableRow: { cursor: "pointer" }
}));

export default function SatisfactionSurveys() {
  const classes = useStyles();
  const history = useHistory();
  const [from, setFrom] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/user-ratings/stats", { params: { from, to, source: source || undefined } });
      setStats(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [from, to, source]);

  useEffect(() => {
    load();
  }, [load]);

  const total = stats?.total || 0;

  return (
    <MainContainer flow>
      <MainHeader>
        <Title>Encuestas de satisfacción</Title>
      </MainHeader>

      <Box className={classes.filters}>
        <TextField
          label="Desde"
          type="date"
          size="small"
          variant="outlined"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Hasta"
          type="date"
          size="small"
          variant="outlined"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
          <InputLabel>Encuesta</InputLabel>
          <Select value={source} onChange={(e) => setSource(e.target.value)} label="Encuesta">
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="ticket">{SOURCE_LABEL.ticket}</MenuItem>
            <MenuItem value="negocio">{SOURCE_LABEL.negocio}</MenuItem>
          </Select>
        </FormControl>
        {loading && <CircularProgress size={24} style={{ alignSelf: "center" }} />}
      </Box>

      {stats && (
        <>
          <Box className={classes.kpis}>
            <Paper variant="outlined" className={classes.kpi}>
              <Typography variant="caption">Respuestas</Typography>
              <Typography className={classes.kpiValue}>{total}</Typography>
            </Paper>
            <Paper variant="outlined" className={classes.kpi}>
              <Typography variant="caption">Promedio (1 a 3)</Typography>
              <Typography className={classes.kpiValue}>{total ? stats.average.toFixed(2) : "—"}</Typography>
            </Paper>
            <Paper variant="outlined" className={classes.kpi}>
              <Typography variant="caption">Satisfechos o muy satisfechos</Typography>
              <Typography className={classes.kpiValue} style={{ color: "#43A047" }}>
                {total ? `${stats.satisfiedPct}%` : "—"}
              </Typography>
            </Paper>
            {stats.bySource.map((s) => (
              <Paper key={s.source} variant="outlined" className={classes.kpi}>
                <Typography variant="caption">{SOURCE_LABEL[s.source] || s.source}</Typography>
                <Typography className={classes.kpiValue}>{s.total}</Typography>
                <Typography variant="caption" color="textSecondary">promedio {s.average.toFixed(2)}</Typography>
              </Paper>
            ))}
          </Box>

          <Paper variant="outlined" className={classes.section}>
            <Typography className={classes.sectionTitle}>Distribución</Typography>
            {[3, 2, 1].map((rate) => {
              const count = stats.distribution[rate] || 0;
              const pct = total ? Math.round((count / total) * 100) : 0;
              return (
                <Box key={rate} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12, marginBottom: 8 }}>
                  <Box minWidth={0} flex="0 1 130px">
                    <Typography variant="body2">{rate} · {RATE_META[rate].label}</Typography>
                  </Box>
                  <div className={classes.barTrack}>
                    <div style={{ width: `${pct}%`, height: "100%", background: RATE_META[rate].color }} />
                  </div>
                  <Box width={80} textAlign="right">
                    <Typography variant="body2">{count} ({pct}%)</Typography>
                  </Box>
                </Box>
              );
            })}
          </Paper>

          {stats.daily.length > 0 && (
            <Paper variant="outlined" className={classes.section}>
              <Typography className={classes.sectionTitle}>Respuestas por día</Typography>
              <div style={{ width: "100%", height: 240, minWidth: 0, overflow: "hidden" }}>
                <ResponsiveContainer>
                  <BarChart data={stats.daily.map((d) => ({ ...d, label: moment(d.day).format("DD/MM") }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <Tooltip formatter={(value, name) => [value, name === "total" ? "Respuestas" : name]} />
                    <Bar dataKey="total" fill="#2196F3" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          )}

          {stats.byAgent.length > 0 && (
            <Paper variant="outlined" className={classes.section}>
              <Typography className={classes.sectionTitle}>Por agente (encuesta de atención)</Typography>
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.cell}>Agente</TableCell>
                    <TableCell align="center">Respuestas</TableCell>
                    <TableCell align="center">Promedio</TableCell>
                    <TableCell align="center">Insatisfecho</TableCell>
                    <TableCell align="center">Satisfecho</TableCell>
                    <TableCell align="center">Muy satisfecho</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.byAgent.map((a) => (
                    <TableRow key={a.userId}>
                      <TableCell>{a.name}</TableCell>
                      <TableCell align="center">{a.total}</TableCell>
                      <TableCell align="center">{a.average.toFixed(2)}</TableCell>
                      <TableCell align="center">{a.r1}</TableCell>
                      <TableCell align="center">{a.r2}</TableCell>
                      <TableCell align="center">{a.r3}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <Paper variant="outlined" className={classes.section}>
            <Typography className={classes.sectionTitle}>Últimas respuestas</Typography>
            {stats.recent.length === 0 ? (
              <Typography variant="body2" color="textSecondary">Sin respuestas en este periodo.</Typography>
            ) : (
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.cell}>Fecha</TableCell>
                    <TableCell>Contacto</TableCell>
                    <TableCell>Encuesta</TableCell>
                    <TableCell>Agente</TableCell>
                    <TableCell align="center">Calificación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recent.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      className={r.ticketUuid ? classes.clickableRow : undefined}
                      onClick={() => r.ticketUuid && history.push(`/tickets/${r.ticketUuid}`)}
                    >
                      <TableCell>{moment.utc(r.createdAt).local().format("DD/MM/YYYY HH:mm")}</TableCell>
                      <TableCell>{r.contactName || r.contactNumber || "—"}</TableCell>
                      <TableCell>{SOURCE_LABEL[r.source] || r.source}</TableCell>
                      <TableCell>{r.userName || "—"}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={RATE_META[r.rate]?.label || r.rate}
                          style={{ backgroundColor: RATE_META[r.rate]?.color, color: "#fff" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}
    </MainContainer>
  );
}
